import { renderToStaticMarkup } from "react-dom/server";
import { ChatAppResponse, getCitationFilePath } from "../../api";
import { QueryPlanStep, getStepLabel, activityTypeLabels } from "../AnalysisPanel/agentPlanUtils";

export type CitationDetail = {
    reference: string;
    index: number;
    isWeb: boolean;
    activityId?: string;
    stepNumber?: number;
    stepLabel?: string;
    stepSource?: string;
};

type CitationFragment =
    | { type: "text"; value: string }
    | {
          type: "citation";
          detail: CitationDetail;
      };

type ActivityStepMeta = {
    stepNumber: number;
    stepLabel: string;
};

type HtmlParsedAnswer = {
    answerHtml: string;
    citations: CitationDetail[];
};

const isWebCitation = (reference: string) => reference.startsWith("http://") || reference.startsWith("https://");

const normalizeCitationKey = (value: string): string =>
    value
        .normalize("NFKC")
        .replace(/[\u2010-\u2015\u2212]/g, "-")
        .replace(/\u00a0/g, " ")
        .trim();

const stripFollowupQuestionMarkers = (value: string): string => value.replace(/(?:\n\s*)?<<[^<>]+>>\s*/g, "").trimEnd();

export function extractFollowupQuestionsFromAnswerText(value: string | undefined): string[] {
    if (!value) {
        return [];
    }
    const questions = new Set<string>();
    for (const match of value.matchAll(/<<([^<>]+)>>/g)) {
        const question = match[1]?.trim();
        if (question) {
            questions.add(question);
        }
    }
    return Array.from(questions);
}

const normalizeAnswerText = (answer: ChatAppResponse, isStreaming: boolean): string => {
    let parsedAnswer = answer.message.content.trim();

    if (isStreaming) {
        let lastIndex = parsedAnswer.length;
        for (let i = parsedAnswer.length - 1; i >= 0; i--) {
            if (parsedAnswer[i] === "]") {
                break;
            } else if (parsedAnswer[i] === "[") {
                lastIndex = i;
                break;
            }
        }
        parsedAnswer = parsedAnswer.substring(0, lastIndex);
    }

    return stripFollowupQuestionMarkers(parsedAnswer);
};

const buildActivityStepMap = (answer: ChatAppResponse): Record<string, ActivityStepMeta> => {
    const mapping: Record<string, ActivityStepMeta> = {};
    const thoughts = answer.context?.thoughts;
    if (!Array.isArray(thoughts)) {
        return mapping;
    }

    const thoughtWithPlan = thoughts.find(thought => Array.isArray(thought.props?.query_plan));
    if (!thoughtWithPlan) {
        return mapping;
    }

    const planSteps = (thoughtWithPlan.props?.query_plan as QueryPlanStep[]) ?? [];
    planSteps.forEach((step, index) => {
        if (step && step.id !== undefined && step.id !== null) {
            mapping[String(step.id)] = {
                stepNumber: index + 1,
                stepLabel: getStepLabel(step)
            };
        }
    });

    return mapping;
};

const collectCitations = (
    answer: ChatAppResponse,
    isStreaming: boolean
): { fragments: CitationFragment[]; citations: CitationDetail[]; citationIds: Record<string, string> } => {
    const citationIds = answer.context.data_points.citation_ids ?? {};
    const possibleCitations = answer.context.data_points.citations || [];
    const citationActivityDetails = answer.context.data_points.citation_activity_details ?? {};
    const activitySteps = buildActivityStepMap(answer);
    const externalResults = answer.context.data_points.external_results_metadata || [];
    const parsedAnswer = normalizeAnswerText(answer, isStreaming);
    const parts = parsedAnswer.split(/\[([^\]]+)\]/g);

    // Helper to resolve SharePoint filename to URL
    const resolveSharePointUrl = (citation: string): string => {
        // If it's already a URL, return as-is
        if (isWebCitation(citation)) {
            return citation;
        }
        // Check if this looks like a filename (has an extension)
        const hasFileExtension = /\.(pdf|docx?|xlsx?|pptx?|txt|html?|csv)$/i.test(citation);
        if (!hasFileExtension) {
            return citation;
        }

        // Look for matching SharePoint URL in external_results_metadata
        // Match by checking if the URL ends with the filename
        const matchingResult = externalResults.find(result => {
            if (!result.url) return false;
            const urlParts = result.url.split("/");
            const urlFilename = urlParts[urlParts.length - 1];
            return urlFilename === citation || decodeURIComponent(urlFilename) === citation;
        });

        return matchingResult?.url || citation;
    };

    const fragments: CitationFragment[] = [];
    const citationMap = new Map<string, CitationDetail>();
    const citationList: CitationDetail[] = [];

    const addCitation = (reference: string, fragmentSink?: CitationFragment[]) => {
        const normalizedReference = normalizeCitationKey(reference);
        const canonicalReference =
            possibleCitations.find(citation => normalizeCitationKey(citation) === normalizedReference) ??
            possibleCitations.find(citation => normalizeCitationKey(citation).endsWith(normalizedReference)) ??
            reference;
        const resolvedReference = resolveSharePointUrl(canonicalReference);
        const existing = citationMap.get(resolvedReference);
        if (existing) {
            fragmentSink?.push({ type: "citation", detail: existing });
            return existing;
        }

        const backendDetail = citationActivityDetails?.[canonicalReference] ?? citationActivityDetails?.[reference];
        const activityId = backendDetail?.id;
        const stepMeta = activityId ? activitySteps[String(activityId)] : undefined;
        const activityLabel = backendDetail?.type ? activityTypeLabels[backendDetail.type] || backendDetail.type : undefined;

        const detail: CitationDetail = {
            reference: resolvedReference,
            index: citationList.length + 1,
            isWeb: isWebCitation(resolvedReference),
            activityId: activityId !== undefined ? String(activityId) : undefined,
            stepNumber: backendDetail?.number ?? stepMeta?.stepNumber,
            stepLabel: activityLabel ?? stepMeta?.stepLabel,
            stepSource: backendDetail?.source
        };

        citationMap.set(resolvedReference, detail);
        citationList.push(detail);
        fragmentSink?.push({ type: "citation", detail });
        return detail;
    };

    parts.forEach((part, index) => {
        if (index % 2 === 0) {
            fragments.push({ type: "text", value: part });
            return;
        }

        const normalizedPart = normalizeCitationKey(part);
        const isValidCitation = possibleCitations.some(citation => normalizeCitationKey(citation).endsWith(normalizedPart));
        if (!isValidCitation) {
            fragments.push({ type: "text", value: `[${part}]` });
            return;
        }

        addCitation(part, fragments);
    });

    possibleCitations.forEach(citation => addCitation(citation));

    return { fragments, citations: citationList, citationIds };
};

const renderCitation = (detail: CitationDetail, citationIds: Record<string, string>, onCitationClicked: (citationFilePath: string) => void) => {
    const stepBadgeLabel = detail.stepSource ?? detail.stepLabel;
    const stepBadgeTitle =
        detail.stepNumber !== undefined
            ? `Linked to Step ${detail.stepNumber}${detail.stepLabel ? `: ${detail.stepLabel}` : ""}${detail.stepSource ? ` (${detail.stepSource})` : ""}`
            : stepBadgeLabel
              ? `Linked to ${stepBadgeLabel}`
              : undefined;
    const supElement = <sup title={stepBadgeTitle ?? undefined}>{detail.index}</sup>;

    if (detail.isWeb) {
        return renderToStaticMarkup(
            <span className="citationBadgeContainer">
                <a
                    className="supContainer"
                    title={detail.reference}
                    href={detail.reference}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                >
                    {supElement}
                </a>
            </span>
        );
    }

    const assessmentId = citationIds[detail.reference];
    const path = assessmentId || getCitationFilePath(detail.reference);
    return renderToStaticMarkup(
        <span className="citationBadgeContainer">
            <a
                className="supContainer"
                title={detail.reference}
                onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onCitationClicked(path);
                }}
            >
                {supElement}
            </a>
        </span>
    );
};

export function parseAnswerToHtml(answer: ChatAppResponse, isStreaming: boolean, onCitationClicked: (citationFilePath: string) => void): HtmlParsedAnswer {
    const { fragments, citations, citationIds } = collectCitations(answer, isStreaming);
    const answerHtml = fragments
        .map(fragment => (fragment.type === "text" ? fragment.value : renderCitation(fragment.detail, citationIds, onCitationClicked)))
        .join("");

    return {
        answerHtml,
        citations
    };
}

export function extractCitationDetails(answer: ChatAppResponse, isStreaming = false): CitationDetail[] {
    return collectCitations(answer, isStreaming).citations;
}
